import type { ExternalToast } from 'sonner';
import { toast as Toaster } from 'sonner';
import type { SettingsState } from '../settings/types';
import { defaultSettings } from '../settings/settings-config';
import { DEFAULT_TOAST_POSITION } from './constants';

type TitleType = (() => React.ReactNode) | React.ReactNode;

let toastSettings: SettingsState = defaultSettings;

export const syncToastSettings = (settings: SettingsState) => {
  toastSettings = settings;
};

type ToastSpacingConfig = {
  marginLeft?: string;
  marginRight?: string;
};

let toastSpacing: ToastSpacingConfig = {};

export const syncToastSpacing = (spacing: ToastSpacingConfig) => {
  toastSpacing = {
    ...toastSpacing,
    ...spacing,
  };
};

const getLeftOffset = () => {
  switch (toastSettings.navLayout) {
    case 'vertical':
      return 'var(--layout-nav-vertical-width)';
    case 'mini':
      return 'var(--layout-nav-mini-width)';
    default:
      return null;
  }
};

const getRightOffset = () => 'var(--layout-dashboard-content-px)';

const applyHorizontalOffsets = (data?: ExternalToast): ExternalToast | undefined => {
  const position = data?.position ?? DEFAULT_TOAST_POSITION;

  const styleUpdates: Record<string, string> = {};

  if (position?.includes('left')) {
    const leftOffset = toastSpacing.marginLeft ?? getLeftOffset();

    if (leftOffset) {
      styleUpdates.left = leftOffset;
    }
  }

  if (position?.includes('right')) {
    const rightOffset = toastSpacing.marginRight ?? getRightOffset();

    if (rightOffset) {
      styleUpdates.right = rightOffset;
    }
  }

  if (!Object.keys(styleUpdates).length) {
    return data;
  }

  const baseData: ExternalToast = data ?? { position };

  return {
    ...baseData,
    position,
    style: {
      ...baseData.style,
      ...styleUpdates,
    },
  };
};

/**
 * Provides a unified toast helper that mirrors the underlying `Toaster` API while
 * automatically applying custom horizontal offsets to every toast variant (success,
 * info, warning, error, custom, message, loading) and preserving access to promise
 * handling, dismissal, and history retrieval.
 */
export const toast = Object.assign(
  (message: TitleType, data?: ExternalToast) => {
    return Toaster(message, applyHorizontalOffsets(data));
  },
  {
    success: (message: TitleType | React.ReactNode, data?: ExternalToast) => {
      return Toaster.success(message, applyHorizontalOffsets(data));
    },
    info: (message: TitleType | React.ReactNode, data?: ExternalToast) => {
      return Toaster.info(message, applyHorizontalOffsets(data));
    },
    warning: (message: TitleType | React.ReactNode, data?: ExternalToast) => {
      return Toaster.warning(message, applyHorizontalOffsets(data));
    },
    error: (message: TitleType | React.ReactNode, data?: ExternalToast) => {
      return Toaster.error(message, applyHorizontalOffsets(data));
    },
    custom: (jsx: (id: number | string) => React.ReactElement, data?: ExternalToast) => {
      return Toaster.custom(jsx, applyHorizontalOffsets(data));
    },
    message: (message: TitleType | React.ReactNode, data?: ExternalToast) => {
      return Toaster.message(message, applyHorizontalOffsets(data));
    },
    promise: <ToastData>(
      promise: Parameters<typeof Toaster.promise<ToastData>>[0],
      data?: Parameters<typeof Toaster.promise<ToastData>>[1]
    ) => {
      return Toaster.promise(promise, data);
    },
    dismiss: (id?: number | string) => {
      return Toaster.dismiss(id);
    },
    loading: (message: TitleType | React.ReactNode, data?: ExternalToast) => {
      return Toaster.loading(message, applyHorizontalOffsets(data));
    },
    getHistory: () => {
      return Toaster.getHistory();
    },
  }
);
